// ─── Keep a count honest while the screen stays open ─────────────────────────
//
// Drea, 2026-10-07: the home screen loaded its unread count once and never
// again, so a message that arrived while the page was open showed only after
// leaving and coming back. This asks again every `everyMs` while the page is
// on screen, and straight away when the person returns to it (the tab or app
// becomes visible, or the window gains focus).
//
// Polling, not push: there is no push channel in this app yet, and a count
// that is at most fifteen seconds old on a page somebody is looking at does
// what was asked. A hidden page asks nothing.
//
// It calls the caller's own `reload`, so the three states of `useAsync`
// (loading, failed, loaded) are unchanged and a failed refresh stays visible.

import { useEffect, useRef } from "react";

export function useLiveRefresh(reload: () => void, everyMs = 15_000): void {
  const latest = useRef(reload);
  latest.current = reload;

  useEffect(() => {
    const refresh = () => { if (document.visibilityState === "visible") latest.current(); };
    const timer = setInterval(refresh, everyMs);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [everyMs]);
}
